/**
 * 钱包地址配置文件
 * 用于管理节点购买收款地址和其他区块链相关配置
 */

const walletConfig = {
  // USDT收款地址列表（用于轮训收款）
  // 优先从环境变量加载，避免硬编码在代码中
  usdtReceiverAddresses: (
    process.env.VUE_APP_USDT_RECEIVER_ADDRESS || '0x341cAeE0e388E471BE52767BD3183bDd3475B1f4'
  ).split(',').map(addr => addr.trim().toLowerCase()).filter(addr => addr), // 统一转为小写以进行不区分大小写的比较
  
  // 从后端API获取最新的收款地址（模拟）
  async fetchRemoteAddresses() {
    try {
      // 实际项目中这里应该是 fetch('https://api.verdict.market/v1/config/addresses')
      // 模拟API延迟
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 模拟返回数据，可以在这里覆盖默认地址
      // const response = await fetch('/api/config/addresses');
      // const data = await response.json();
      // if (data && data.addresses) {
      //   this.usdtReceiverAddresses = data.addresses.map(addr => addr.toLowerCase());
      //   console.log('已从远程服务器更新收款地址列表');
      // }
      
      return true;
    } catch (error) {
      console.error('获取远程地址配置失败:', error);
      return false;
    }
  },

  // 当前使用的地址索引（用于轮训）
  currentAddressIndex: 0,
  
  // 获取下一个收款地址（轮训逻辑）
  getNextReceiverAddress() {
    const address = this.usdtReceiverAddresses[this.currentAddressIndex];
    // 更新索引为下一个地址
    this.currentAddressIndex = (this.currentAddressIndex + 1) % this.usdtReceiverAddresses.length;
    // console.log(`使用收款地址 ${this.currentAddressIndex + 1}/${this.usdtReceiverAddresses.length}: ${address}`);
    return address; // 返回已经是小写的地址，或者根据需要转换格式（通常钱包不区分大小写， checksum地址除外）
  },
  
  // 获取当前收款地址
  getCurrentReceiverAddress() {
    return this.usdtReceiverAddresses[this.currentAddressIndex];
  },
  
  // 设置特定地址索引
  setAddressIndex(index) {
    if (index >= 0 && index < this.usdtReceiverAddresses.length) {
      this.currentAddressIndex = index;
      console.log(`已切换到收款地址 ${index + 1}: ${this.usdtReceiverAddresses[index]}`);
      return true;
    }
    console.warn(`无效的地址索引: ${index}, 可用范围: 0-${this.usdtReceiverAddresses.length - 1}`);
    return false;
  },
  
  // 添加新的收款地址
  addReceiverAddress(address) {
    const lowerAddr = address.toLowerCase();
    if (!this.usdtReceiverAddresses.includes(lowerAddr)) {
      this.usdtReceiverAddresses.push(lowerAddr);
      console.log(`已添加收款地址 ${this.usdtReceiverAddresses.length}: ${address}`);
      return true;
    }
    console.warn(`地址已存在: ${address}`);
    return false;
  },
  
  // 移除收款地址
  removeReceiverAddress(index) {
    if (index >= 0 && index < this.usdtReceiverAddresses.length) {
      const removed = this.usdtReceiverAddresses.splice(index, 1)[0];
      // 调整当前索引
      if (this.currentAddressIndex >= index && this.currentAddressIndex > 0) {
        this.currentAddressIndex--;
      }
      if (this.currentAddressIndex >= this.usdtReceiverAddresses.length) {
        this.currentAddressIndex = 0;
      }
      console.log(`已移除收款地址 ${index + 1}: ${removed}`);
      return true;
    }
    return false;
  },
  
  // BSC网络上的USDT合约地址
  usdtContractAddress: '0x55d398326f99059fF775485246999027B3197955',
  
  // 支持的区块链网络配置
  networks: {
    bsc: {
      name: 'Binance Smart Chain',
      chainId: 56,
      rpcUrl: 'https://bsc-dataseed.binance.org/',
      explorerUrl: 'https://bscscan.com',
      usdtContractAddress: '0x55d398326f99059fF775485246999027B3197955'
    },
    bscTestnet: {
      name: 'BSC Testnet',
      chainId: 97,
      rpcUrl: 'https://data-seed-prebsc-1-s1.binance.org:8545/',
      explorerUrl: 'https://testnet.bscscan.com',
      usdtContractAddress: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd' // 测试网USDT
    }
  },
  
  // 当前使用的网络
  currentNetwork: 'bsc',
  
  // 获取当前网络配置
  getCurrentNetwork() {
    return this.networks[this.currentNetwork];
  },
  
  // 切换网络
  switchNetwork(network) {
    if (this.networks[network]) {
      this.currentNetwork = network;
      console.log(`已切换到网络: ${this.networks[network].name}`);
      return true;
    }
    console.warn(`未知网络: ${network}`);
    return false;
  },
  
  // 获取当前网络的USDT合约地址
  getCurrentUSDTContractAddress() {
    return this.getCurrentNetwork().usdtContractAddress || this.usdtContractAddress;
  },
  
  // 验证以太坊地址格式
  isValidEthereumAddress(address) {
    if (!address || typeof address !== 'string') {
      return false;
    }
    
    // 检查地址长度（0x前缀 + 40个十六进制字符）
    if (!address.startsWith('0x') || address.length !== 42) {
      return false;
    }
    
    // 检查是否为有效的十六进制字符串
    const hexRegex = /^0x[0-9a-fA-F]{40}$/;
    return hexRegex.test(address);
  },
  
  // 检查地址是否在白名单中（允许的收款地址）
  isAllowedAddress(address) {
    if (!this.isValidEthereumAddress(address)) {
      return false;
    }
    
    // 检查地址是否在预定义的白名单中
    return this.usdtReceiverAddresses.includes(address.toLowerCase());
  },
  
  // 验证并获取安全的收款地址
  getValidatedReceiverAddress(address = null) {
    const targetAddress = address || this.getNextReceiverAddress();
    
    // 验证地址格式
    if (!this.isValidEthereumAddress(targetAddress)) {
      console.error(`无效的以太坊地址格式: ${targetAddress}`);
      throw new Error('无效的收款地址格式');
    }
    
    // 检查地址是否在白名单中
    if (!this.isAllowedAddress(targetAddress)) {
      console.warn(`警告：地址不在白名单中: ${targetAddress}`);
      // 这里可以选择是否抛出错误，或者只是警告
      // 为了安全，我们抛出错误
      throw new Error('收款地址不在白名单中，可能存在安全风险');
    }
    
    console.log(`已验证的安全收款地址: ${targetAddress}`);
    return targetAddress;
  },
  
  // 获取所有收款地址信息
  getReceiverAddressesInfo() {
    return {
      total: this.usdtReceiverAddresses.length,
      currentIndex: this.currentAddressIndex,
      currentAddress: this.getCurrentReceiverAddress(),
      allAddresses: [...this.usdtReceiverAddresses],
      securityInfo: {
        addressesValidated: this.usdtReceiverAddresses.every(addr => this.isValidEthereumAddress(addr)),
        hasSecurityChecks: true
      }
    };
  }
};

export default walletConfig;
